<template>
  <div class="task-history-container">
    <a-table
      :columns="columns"
      :data-source="taskHistoryData"
      :loading="isFetchingTaskHistory"
      :pagination="false"
      row-key="history_id"
      size="small"
      class="task-history-table"
      :locale="{
        emptyText:
          'No task history found. Changes to the task will appear here.',
      }"
    >
      <template #title>
        <div
          class="d-flex justify-content-between align-items-center task-history-header"
        >
          <div class="d-flex align-items-center">
            <h4 class="mb-0 task-history-title">Task History</h4>
            <a-tag color="blue" class="ml-2"
              >{{ taskHistoryData.length }} records</a-tag
            >
          </div>
          <a-button
            @click="refreshHistory"
            :loading="isFetchingTaskHistory"
            type="primary"
            size="small"
            class="refresh-btn"
          >
            <template #icon>
              <reload-outlined />
            </template>
            Refresh
          </a-button>
        </div>
      </template>

      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'history_date'">
          <span>{{ formatDate(record.history_date) }}</span>
        </template>

        <template v-if="column.key === 'history_type'">
          <a-tag :color="getHistoryTypeColor(record.history_type)">
            {{ record.history_type }}
          </a-tag>
        </template>

        <template v-if="column.key === 'taskName'">
          <span class="font-weight-bold">{{ record.taskName }}</span>
        </template>

        <template v-if="column.key === 'actions'">
          <a-space>
            <a-tooltip title="View Details">
              <a-button
                type="link"
                size="small"
                @click="viewHistoryDetails(record)"
              >
                <template #icon>
                  <eye-outlined />
                </template>
              </a-button>
            </a-tooltip>
            <a-tooltip title="Revert to this version">
              <a-button
                type="link"
                size="small"
                danger
                :loading="isRevertingTask"
                @click="handleRevertTask(record)"
              >
                <!-- @click="showRevertConfirm(record)" -->
                <template #icon>
                  <rollback-outlined />
                </template>
              </a-button>
            </a-tooltip>
          </a-space>
        </template>
      </template>
    </a-table>

    <!-- History Details Modal -->
    <a-modal
      v-model:visible="showDetailsModal"
      title="Task History Details"
      width="80vw"
      :footer="null"
      destroy-on-close
    >
      <div v-if="selectedHistoryRecord" class="history-details-container">
        <!-- Basic Information -->
        <div class="info-section">
          <div class="section-title">Basic Information</div>
          <div class="detail-item">
            <div class="detail-label">Version:</div>
            <div class="detail-value">
              #{{ selectedHistoryRecord.history_id }}
            </div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Task Name:</div>
            <div class="detail-value">
              {{ selectedHistoryRecord.taskName || 'N/A' }}
            </div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Change Date:</div>
            <div class="detail-value">
              {{ formatDate(selectedHistoryRecord.history_date) }}
            </div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Change Type:</div>
            <div class="detail-value">
              <a-tag
                :color="getHistoryTypeColor(selectedHistoryRecord.history_type)"
              >
                {{ getHistoryTypeText(selectedHistoryRecord.history_type) }}
              </a-tag>
            </div>
          </div>
        </div>

        <!-- Task Description -->
        <div v-if="selectedHistoryRecord.taskDescription" class="info-section">
          <div class="section-title">Task Description</div>
          <div class="task-description">
            {{ selectedHistoryRecord.taskDescription }}
          </div>
        </div>

        <!-- Cycle Time Configuration -->
        <div class="info-section">
          <div class="section-title">Cycle Time Configuration</div>
          <div class="parameter-grid">
            <div class="parameter-card">
              <div class="parameter-title">Minimum Cycle Time</div>
              <div>
                {{ formatCycleTime(selectedHistoryRecord.minimum_cycle_time) }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Maximum Cycle Time</div>
              <div>
                {{ formatCycleTime(selectedHistoryRecord.maximum_cycle_time) }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Min Steps Required (%)</div>
              <div>
                {{
                  formatPercentage(
                    selectedHistoryRecord.min_percentage_of_steps_required_to_end_cycle
                  )
                }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Temporal Smoothness</div>
              <div>
                {{ selectedHistoryRecord.temporal_smoothness || 'N/A' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Detection Settings -->
        <div class="info-section">
          <div class="section-title">Detection Settings</div>
          <div class="parameter-grid">
            <div class="parameter-card">
              <div class="parameter-title">Threshold</div>
              <div>{{ formatDecimal(selectedHistoryRecord.threshold) }}</div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Verify Threshold</div>
              <div>
                {{ formatDecimal(selectedHistoryRecord.verify_threshold) }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Object Rotation</div>
              <div>
                {{ formatBoolean(selectedHistoryRecord.object_rotation) }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Multi Process</div>
              <div>
                {{ formatBoolean(selectedHistoryRecord.is_multi_process) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Advanced Configuration -->
        <div v-if="hasAdvancedSettings" class="info-section">
          <div class="section-title">Advanced Configuration</div>
          <div class="parameter-grid">
            <div class="parameter-card">
              <div class="parameter-title">Step Order Important</div>
              <div>
                {{
                  formatBoolean(selectedHistoryRecord.is_step_order_important)
                }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Background Time Include</div>
              <div>
                {{
                  formatBoolean(
                    selectedHistoryRecord.add_background_time_to_step_time
                  )
                }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Spatial Model Iterations</div>
              <div>
                {{ selectedHistoryRecord.spatial_model_iterations || 'N/A' }}
              </div>
            </div>
            <div class="parameter-card">
              <div class="parameter-title">Contextual Model Batch Size</div>
              <div>
                {{ selectedHistoryRecord.contextual_model_batch_size || 'N/A' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import {
  EyeOutlined,
  ReloadOutlined,
  RollbackOutlined,
} from '@ant-design/icons-vue';
import { Modal } from 'ant-design-vue';
import { mapActions, mapGetters } from 'vuex';
import types from 'src/store/mutation-types';

export default {
  name: 'TaskHistory',
  components: {
    ReloadOutlined,
    EyeOutlined,
    RollbackOutlined,
  },

  data() {
    return {
      showDetailsModal: false,
      selectedHistoryRecord: null,
      columns: [
        {
          title: 'Version',
          dataIndex: 'history_id',
          key: 'history_id',
          width: 80,
          sorter: (a, b) => a.history_id - b.history_id,
          render: (text) => `#${text}`,
        },
        {
          title: 'Task Name',
          dataIndex: 'taskName',
          key: 'taskName',
          width: 180,
          ellipsis: true,
        },
        // {
        //   title: 'Change Type',
        //   dataIndex: 'history_type',
        //   key: 'history_type',
        //   width: 120,
        //   filters: [
        //     { text: 'Created', value: '+' },
        //     { text: 'Updated', value: '~' },
        //     { text: 'Deleted', value: '-' },
        //   ],
        //   onFilter: (value, record) => record.history_type === value,
        // },
        {
          title: 'Change Date',
          dataIndex: 'history_date',
          key: 'history_date',
          width: 180,
          sorter: (a, b) => new Date(a.history_date) - new Date(b.history_date),
          defaultSortOrder: 'descend',
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 120,
          align: 'center',
        },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'taskHistoryData',
      'isFetchingTaskHistory',
      'isRevertingTask',
      'selectedTask',
    ]),

    hasAdvancedSettings() {
      if (!this.selectedHistoryRecord) return false;
      const record = this.selectedHistoryRecord;
      return (
        record.is_step_order_important !== null ||
        record.add_background_time_to_step_time !== null ||
        record.spatial_model_iterations !== null ||
        record.contextual_model_batch_size !== null
      );
    },
  },

  mounted() {
    if (this.selectedTask) {
      this.fetchTaskHistory(this.selectedTask);
    }
  },

  methods: {
    ...mapActions(['fetchTaskHistory', 'revertTaskToHistory', 'showModal']),

    refreshHistory() {
      if (this.selectedTask) {
        this.fetchTaskHistory(this.selectedTask);
      }
    },

    viewHistoryDetails(record) {
      this.selectedHistoryRecord = record;
      this.showDetailsModal = true;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleString();
      } catch (error) {
        return 'Invalid Date';
      }
    },

    getHistoryTypeColor(historyType) {
      switch (historyType) {
        case '+':
          return 'green';
        case '~':
          return 'blue';
        case '-':
          return 'red';
        default:
          return 'default';
      }
    },

    getHistoryTypeText(historyType) {
      switch (historyType) {
        case '+':
          return 'Created';
        case '~':
          return 'Modified';
        case '-':
          return 'Deleted';
        default:
          return 'Unknown';
      }
    },

    formatCycleTime(time) {
      if (time === null || time === undefined) return 'N/A';
      return `${time} seconds`;
    },

    formatPercentage(value) {
      if (value === null || value === undefined) return 'N/A';
      return `${value}%`;
    },

    formatDecimal(value) {
      if (value === null || value === undefined) return 'N/A';
      return parseFloat(value).toFixed(2);
    },

    formatBoolean(value) {
      if (value === null || value === undefined) return 'N/A';
      return value ? 'Yes' : 'No';
    },

    async handleRevertTask(record) {
      try {
        const success = await this.revertTaskToHistory({
          taskId: this.selectedTask,
          historyId: record.history_id,
        });

        if (success) {
          // Close the modal after successful revert using the parent's close method
          this.$emit('close');
          // Also trigger the store action as fallback
          this.showModal({
            modalType: types.SET_SHOW_TASK_HISTORY_MODAL,
            value: false,
          });
        }
      } catch (error) {
        console.error('Error reverting task:', error);
      }
    },
  },
};
</script>

<style scoped>
.task-history-container {
  padding: 16px;
}

.task-history-header {
  margin-bottom: 16px;
}

.task-history-title {
  color: #262626;
  font-size: 16px;
  font-weight: 600;
}

.refresh-btn {
  font-weight: 500;
}

/* Task History Table - Match existing theme */
:deep(.task-history-table .ant-table-thead > tr > th) {
  background-color: #fafafa;
  font-weight: 600;
  font-size: 13px;
  color: #595959;
}

:deep(.task-history-table .ant-table-tbody > tr:hover > td) {
  background-color: #f0f9ff;
}

:deep(.task-history-table .ant-table-tbody > tr > td) {
  padding: 12px 16px;
  font-size: 13px;
}

/* Modal Content Styling */
.history-details-container {
  max-height: 70vh;
  overflow-y: auto;
}

.info-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.detail-label {
  font-weight: 600;
  color: #595959;
  min-width: 200px;
  flex-shrink: 0;
}

.detail-value {
  color: #262626;
  flex-grow: 1;
  word-break: break-word;
}

.task-description {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  border-left: 4px solid #1890ff;
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.5;
}

.no-data {
  color: #8c8c8c;
  font-style: italic;
}

.parameter-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
}

@media (max-width: 768px) {
  .parameter-grid {
    grid-template-columns: 1fr;
  }
}

.parameter-card {
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
}

.parameter-title {
  font-weight: 600;
  color: #1890ff;
  font-size: 13px;
  margin-bottom: 8px;
}
</style>
